import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Linkedin, Twitter, Facebook, Mail } from 'lucide-react';
import { supabase } from '../../lib/supabase';

export default function Footer() {
  const [logo, setLogo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const { data, error } = await supabase
          .from('site_settings')
          .select('value')
          .eq('key', 'logo')
          .maybeSingle();
        
        if (error) {
          console.error('Error fetching logo:', error);
          setError('Failed to load logo');
          return;
        }
        
        if (data?.value) {
          setLogo(data.value);
        }
      } catch (err) {
        console.error('Error in logo fetch:', err);
        setError('Failed to load logo');
      }
    };

    fetchLogo();
  }, []);

  return (
    <footer className="bg-[#30475e] text-white pt-16 pb-8">
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              {error ? (
                <span className="text-2xl font-title font-bold bg-gradient-to-r from-[#d03484] to-[#d47254] bg-clip-text text-transparent">recruteurs.io</span>
              ) : (
                <img 
                  src="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/assets//Recruteurs.io%20Logo%20Final-02.png" 
                  alt="recruteurs.io" 
                  className="h-8" 
                />
              )}
            </Link>
            <p className="text-neutral-400 mb-6">
              La <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">seule</span> agence RH qui révolutionne le recrutement en combinant <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">expertise</span> RH et <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">technologies</span> innovantes.
            </p>
            <div className="flex space-x-4">
              {[
                { name: 'LinkedIn', icon: <Linkedin size={18} />, url: 'https://linkedin.com/company/recruteurs-io' },
                { name: 'Twitter', icon: <Twitter size={18} />, url: 'https://twitter.com/recruteurs_io' },
                { name: 'Facebook', icon: <Facebook size={18} />, url: 'https://facebook.com/recruteurs.io' },
                { name: 'Email', icon: <Mail size={18} />, url: 'mailto:contact@recruteurs.io' },
              ].map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-white hover:text-primary transition-colors"
                  aria-label={social.name}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>
          
          {[
            {
              title: 'Entreprise',
              links: [
                { label: 'Recrutement', url: '/entreprise/recrutement' },
                { label: 'Campagnes', url: '/entreprise/campagnes' },
                { label: 'Externalisation', url: '/entreprise/externalisation' },
                { label: 'IA', url: '/entreprise/ia' },
              ],
            },
            {
              title: 'Candidat',
              links: [
                { label: 'Offres', url: '/candidat/offres' },
                { label: 'Pourquoi recruteurs ?', url: '/candidat/pourquoi' },
                { label: 'Cooptation', url: '/candidat/cooptation' },
                { label: 'Talent AI', url: '/candidat/talentai' },
              ],
            },
            {
              title: 'Infos',
              links: [
                { label: 'Questions fréquentes', url: '/infos/questions' },
                { label: 'Charte', url: '/infos/charte' },
                { label: 'Tarifs', url: '/infos/tarifs' },
                { label: 'Contact', url: '/infos/contact' },
              ],
            },
            {
              title: 'Ressources',
              links: [
                { label: 'Articles', url: '/ressources/articles' },
                { label: 'Outils', url: '/ressources/outils' },
                { label: 'Ressources téléchargeables', url: '/ressources/ressources' },
              ],
            },
          ].map((column) => (
            <div key={column.title}>
              <h4 className="text-lg font-bold mb-4 bg-gradient-to-r from-[#d03484] to-[#d47254] bg-clip-text text-transparent">{column.title}</h4>
              <ul className="space-y-2">
                {column.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.url}
                      className="text-neutral-400 hover:text-white transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        
        <hr className="border-neutral-700 my-8" />
        
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-neutral-500 text-sm">
            © {currentYear} recruteurs.io. Tous droits réservés.
          </div>
          <div className="flex space-x-6">
            <Link to="/legal/mentions-legales" className="text-neutral-500 text-sm hover:text-white transition-colors">
              Mentions légales
            </Link>
            <Link to="/legal/confidentialite" className="text-neutral-500 text-sm hover:text-white transition-colors">
              Politique de confidentialité
            </Link>
            <Link to="/legal/cgv" className="text-neutral-500 text-sm hover:text-white transition-colors">
              CGV
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}